import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Star, FileText } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { Loading } from '../../../components/Shared/Loading';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../../components/Shared/Table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/Shared/Tabs';
import { useTextSnippetStore } from '../../../store/useTextSnippetStore';
import type { TextSnippetCategory, TextSnippetDto } from '../../../types/textSnippets';
import { TEXT_SNIPPET_CATEGORIES } from '../../../types/textSnippets';
import { renderSample } from '../../../utils/snippetSample';
import TextSnippetForm from './TextSnippetForm';

function preview(body: string, max = 80): string {
  const single = body.replace(/\s+/g, ' ').trim();
  return single.length > max ? single.slice(0, max) + '…' : single;
}

export default function TextSnippetsPage() {
  const { t } = useTranslation(['textSnippets', 'common']);
  const navigate = useNavigate();
  const {
    items,
    loading,
    error,
    fetchSnippets,
    deleteSnippet,
    setDefault,
  } = useTextSnippetStore();

  const [activeCategory, setActiveCategory] = useState<TextSnippetCategory>(
    'OfferIntroduction'
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<TextSnippetDto | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TextSnippetDto | null>(null);

  useEffect(() => {
    fetchSnippets();
  }, [fetchSnippets]);

  const byCategory = useMemo(() => {
    const groups = Object.fromEntries(
      TEXT_SNIPPET_CATEGORIES.map((cat) => [cat, [] as TextSnippetDto[]]),
    ) as Record<TextSnippetCategory, TextSnippetDto[]>;
    for (const item of items) {
      groups[item.category]?.push(item);
    }
    return groups;
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (snippet: TextSnippetDto) => {
    setEditing(snippet);
    setIsFormOpen(true);
  };

  const askDelete = (snippet: TextSnippetDto) => {
    setToDelete(snippet);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteSnippet(toDelete.id);
    } finally {
      setIsDeleteOpen(false);
      setToDelete(null);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/settings')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('common:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('textSnippets:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('textSnippets:subtitle')}
            </p>
          </div>
          <Button
            onClick={openCreate}
            leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            {t('textSnippets:actions.create')}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <Tabs
          value={activeCategory}
          onValueChange={(v) => setActiveCategory(v as TextSnippetCategory)}
        >
          <div className="px-3 sm:px-4">
            <TabsList>
              {TEXT_SNIPPET_CATEGORIES.map((cat) => (
                <TabsTrigger key={cat} value={cat}>
                  {t(`textSnippets:categories.${cat}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {TEXT_SNIPPET_CATEGORIES.map((cat) => {
            const rows = byCategory[cat];
            return (
              <TabsContent key={cat} value={cat} className="pt-0">
                {loading && items.length === 0 ? (
                  <div className="p-4"><Loading /></div>
                ) : error ? (
                  <div className="p-4">
                    <EmptyState
                      variant="error"
                      title={t('textSnippets:errors.fetchFailed')}
                      onRetry={() => fetchSnippets()}
                    />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="p-4">
                    <EmptyState
                      icon={<FileText className="w-12 h-12" strokeWidth={1.75} />}
                      title={t('textSnippets:empty')}
                      action={
                        <Button
                          onClick={openCreate}
                          leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                        >
                          {t('textSnippets:actions.create')}
                        </Button>
                      }
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>{t('textSnippets:fields.name')}</TableHeader>
                        <TableHeader className="hidden md:table-cell">
                          {t('textSnippets:fields.preview')}
                        </TableHeader>
                        <TableHeader className="hidden sm:table-cell">
                          {t('textSnippets:fields.sortOrder')}
                        </TableHeader>
                        <TableHeader className="text-right">
                          {t('common:actions.title')}
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((s) => (
                        <TableRow
                          key={s.id}
                          className={s.isActive ? '' : 'opacity-60'}
                        >
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className="text-[13px] font-medium text-slate-900">{s.name}</span>
                              <div className="flex flex-wrap gap-1">
                                {s.isDefault && (
                                  <Badge variant="success">
                                    {t('textSnippets:badges.default')}
                                  </Badge>
                                )}
                                {!s.isActive && (
                                  <Badge variant="default">
                                    {t('textSnippets:badges.inactive')}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-500 md:hidden">
                                {preview(renderSample(s.body, t, s.category), 60)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-[12px] text-slate-500">
                            {preview(renderSample(s.body, t, s.category))}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-[13px] text-slate-600 tabular-nums">
                            {s.sortOrder}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!s.isDefault && s.isActive && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title={t('textSnippets:actions.setDefault')}
                                  aria-label={t('textSnippets:actions.setDefault')}
                                  className="p-1.5"
                                  onClick={() => setDefault(s.id)}
                                >
                                  <Star className="w-4 h-4" strokeWidth={2} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                title={t('textSnippets:actions.edit')}
                                aria-label={t('textSnippets:actions.edit')}
                                className="p-1.5"
                                onClick={() => openEdit(s)}
                              >
                                <Pencil className="w-4 h-4" strokeWidth={2} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title={t('textSnippets:actions.delete')}
                                aria-label={t('textSnippets:actions.delete')}
                                className="p-1.5"
                                onClick={() => askDelete(s)}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      <TextSnippetForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        snippet={editing}
        defaultCategory={activeCategory}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t('textSnippets:messages.deleteTitle')}
        message={t('textSnippets:messages.deleteConfirm')}
        confirmText={t('textSnippets:actions.delete')}
        variant="danger"
      />
    </div>
  );
}
